import {computed, inject, Injectable, signal} from '@angular/core';
import {CallStatisticService, CallStatisticSummary, QueueLiveStatistics} from '../../../../api';
import {lastValueFrom} from 'rxjs';
import {injectQuery} from '@tanstack/angular-query-experimental';
import {QueryKey} from '../../../constants/query-key';

export interface CallStatisticFilter {
  startDate: Date | null;
  endDate: Date | null;
}

@Injectable({providedIn: 'root'})
export class CallStatisticStoreService {
  private readonly callStatisticService = inject(CallStatisticService);

  filterParams = signal<CallStatisticFilter>({startDate: null, endDate: null});

  // The live counters are only fetched while the dialog is open.
  isLiveRequested = signal(false);

  callStatisticsQuery = injectQuery(() => ({
    queryKey: [QueryKey.CALL_STATISTICS, this.filterParams()],
    queryFn: () => lastValueFrom(this.callStatisticService.getCallStatistics(
      this.filterParams().startDate!,
      this.filterParams().endDate!)),
    enabled: this._hasDates,
  }));
  callStatistics = computed<CallStatisticSummary[]>(() => this.callStatisticsQuery.data()?.callStatistics ?? []);
  isLoading = computed(() => this.callStatisticsQuery.isLoading());

  // Never served from cache: the point of a live read is the value of the moment.
  liveQuery = injectQuery(() => ({
    queryKey: [QueryKey.CALL_STATISTICS_LIVE],
    queryFn: () => lastValueFrom(this.callStatisticService.getLiveCallStatistics()),
    enabled: this.isLiveRequested(),
    staleTime: 0,
  }));
  liveStatistics = computed<QueueLiveStatistics | null>(() => this.liveQuery.data()?.callStatistics ?? null);
  isLoadingLive = computed(() => this.liveQuery.isFetching());
  isLiveError = computed(() => this.liveQuery.isError());

  private get _hasDates() {
    const {startDate, endDate} = this.filterParams();

    return startDate != null && endDate != null;
  }
}
